import { useQuery } from '@tanstack/react-query';
import { FC, useMemo, useState } from 'react';

import { _getEntityViewsTeamAssociates } from '@/api/entity-views/queries';
import { useCreateRoleRequest } from '@/api/role-requests/mutations';
import { RoleRequestType } from '@/api/role-requests/types';
import SearchIcon from '@/assets/svg/ui-elements/search.svg';
import useDebounce from '@/global-hooks/use-debounce';
import {
  Checkbox,
  EmptyState,
  Input,
  Loader,
  Modal,
  theme,
  Typography,
} from '@/shared/design-system';

import { CandidateList, CandidateRow, CenteredState, ModalBody } from '../styled';

export const NO_CANDIDATES_MESSAGE = 'Everyone on the team already holds this role';

/** The team list is paginated; this is the page size the picker asks for. */
const CANDIDATE_PAGE_SIZE = 50;

export type AddMembersModalProps = {
  open: boolean;
  onClose: () => void;
  entityId?: string | null;
  roleId: string;
  roleName: string;
  existingMemberIds: string[];
};

export const AddMembersModal: FC<AddMembersModalProps> = ({
  open,
  onClose,
  entityId,
  roleId,
  roleName,
  existingMemberIds,
}) => {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const debouncedSearch = useDebounce(search, 300);

  const team = useQuery({
    ..._getEntityViewsTeamAssociates({
      id: entityId ?? null,
      search: debouncedSearch || undefined,
      limit: CANDIDATE_PAGE_SIZE,
    }),
    enabled: open && !!entityId,
  });

  const { mutateAsync: requestRoleChange, isPending } = useCreateRoleRequest({ entityId });

  /**
   * Invitees who have not signed up yet have no `userId`, so there is nothing to
   * assign a role to — the backend keys membership on the user.
   */
  const candidates = useMemo(
    () =>
      (team.data?.items ?? []).filter(
        (item) => item.userId && !existingMemberIds.includes(item.userId),
      ),
    [team.data, existingMemberIds],
  );

  const close = () => {
    setSelected([]);
    setSearch('');
    onClose();
  };

  const submit = async () => {
    // Sequential rather than parallel: the last-admin and grant-what-you-hold
    // checks are evaluated per request, and a partial failure should stop rather
    // than race.
    for (const userId of selected) {
      try {
        await requestRoleChange({
          type: RoleRequestType.AssignRole,
          payload: { roleId, userId },
        });
      } catch {
        return;
      }
    }

    close();
  };

  const toggle = (userId: string) =>
    setSelected((current) =>
      current.includes(userId) ? current.filter((id) => id !== userId) : [...current, userId],
    );

  return (
    <Modal
      open={open}
      onOpenChange={(isOpen) => !isOpen && close()}
      title={`Add members to ${roleName}`}
      description="Assigning a role requires holding every permission it grants."
      primaryAction={{
        label: isPending ? 'Adding…' : `Add ${selected.length || ''}`.trim(),
        onClick: submit,
      }}
      secondaryAction={{ label: 'Cancel', onClick: close }}
    >
      <ModalBody>
        <Input
          size="sm"
          value={search}
          placeholder="Search team members"
          startIcon={<SearchIcon />}
          aria-label="Search team members"
          onChange={(event) => setSearch(event.target.value)}
        />

        {team.isLoading ? (
          <CenteredState>
            <Loader />
          </CenteredState>
        ) : candidates.length === 0 ? (
          <EmptyState variant="tertiary" title={NO_CANDIDATES_MESSAGE} />
        ) : (
          <CandidateList>
            {candidates.map((candidate) => {
              const userId = candidate.userId as string;

              return (
                <CandidateRow key={userId}>
                  <Checkbox
                    checked={selected.includes(userId)}
                    onCheckedChange={() => toggle(userId)}
                  />
                  <Typography variant="body3" color={theme.colors.text.primary}>
                    {candidate.name ?? candidate.alias ?? candidate.email}
                  </Typography>
                  <Typography variant="body4" color={theme.colors.text.tertiary} noWrap>
                    {candidate.email}
                  </Typography>
                </CandidateRow>
              );
            })}
          </CandidateList>
        )}
      </ModalBody>
    </Modal>
  );
};
